import { Injectable, Injector, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityInterface,
  AutomataDevelopmentService,
  AutomationInterface,
  DataType,
  EntityType,
  OutputParameter,
  WorkflowStepInterface,
} from '@vendasta/automata';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { TRIGGER_STEP_ID } from '../../../../common/constants';
import { convertEntityTypeToDataType } from '../../../../shared/entities';
import { WORKFLOW_TRIGGERS, WorkflowTriggerInterface } from '../../../component-loader/trigger-definitions';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { getLargestTimeUnit, TimeUnit, toTimeUnit } from '../../common/controls/delay-control/delay-control.component';
import { extractWorkflowStepParameterKeyFromReplacementTag } from '../../common/utils';
import { extractWorkflowStepIdFromWaitForTriggerFilter } from '../../filters/filter-data/filter-data-selector/filter-data.service';
import { TaskDefinition } from '@vendasta/automata/lib/_internal/objects';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class DelayUntilEventService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private translateService: TranslateService,
    private injector: Injector,
    private sanitizer: DomSanitizer,
    private readonly automataDevService: AutomataDevelopmentService,
  ) {}

  getActivitySubtitle(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): Observable<string> {
    const step = activity?.workflowStep;

    const defaultNextStepId = activity?.workflowStep?.choices?.defaultNextStep;
    const actualNextStepId = activity?.nextWorkflowStep?.id;
    if (defaultNextStepId === actualNextStepId) {
      const nanos = step?.sleep || 0;
      const timeUnit = getLargestTimeUnit(nanos);
      const sleepInTimeUnit = toTimeUnit(nanos, timeUnit);

      // Return text appropriate to the current time unit, defaulting to hours if a value is missed (e.g. new time unit added)
      // Nanos are assumed never to be used.
      if (sleepInTimeUnit > 0) {
        let params = {};
        const keys = {};
        if (sleepInTimeUnit === 1) {
          keys[TimeUnit.Minute] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.WAITED_ONE_MINUTE';
          keys[TimeUnit.Hour] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.WAITED_ONE_HOUR';
          keys[TimeUnit.Day] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.WAITED_ONE_DAY';
        } else {
          keys[TimeUnit.Minute] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.WAITED_MINUTES';
          keys[TimeUnit.Hour] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.WAITED_HOURS';
          keys[TimeUnit.Day] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.WAITED_DAYS';
          params = { time: sleepInTimeUnit };
        }
        return this.translateService.stream(keys[timeUnit] || keys[TimeUnit.Hour], params);
      }
    }

    // trigger text
    const comp = this.getWaitForTriggerTaskDefinitionFromStep(step);
    if (comp) {
      const service = this.injector.get<CommonDisplayActivityService>(comp.activityDisplayService);
      if (service) {
        return service.getActivitySubtitle(namespace, activity, automation);
      }
    }

    // default
    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const defaultNextStepId = activity?.workflowStep?.choices?.defaultNextStep;
    const choiceIfEventHappened = activity?.workflowStep?.choices?.rules?.[0]?.next;
    const actualNextStepId = activity?.nextWorkflowStep?.id;
    if (defaultNextStepId === actualNextStepId && choiceIfEventHappened !== actualNextStepId) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.EVENT_DID_NOT_HAPPEN');
    }
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.ACTIVITY.EVENT_HAPPENED');
  }

  getActionSubtitleSwitcher(
    namespace: string,
    step: WorkflowStepInterface,
    isTemplate: boolean,
    automation: AutomationInterface,
  ): Observable<string> {
    const displayText: Observable<string>[] = [];

    // trigger text
    const comp = this.getWaitForTriggerTaskDefinitionFromStep(step);
    if (comp) {
      const service = this.injector.get<CommonDisplayActionService>(comp.actionDisplayService);
      if (service) {
        if (isTemplate) {
          displayText.push(service.getTemplateActionSubtitle(namespace, step, true));
        } else {
          displayText.push(service.getActionSubtitle(namespace, step, true, automation));
        }
      }
    }

    displayText.push(this.getSleepDurationText(step?.sleep || 0));
    displayText.push(this.getWaitForDataDisplay(automation, step?.waitForTriggerFilter?.value));

    if (displayText.length > 0) {
      return combineLatest(displayText).pipe(
        map((dt) => {
          let resultHTML = '';
          dt.forEach((text) => {
            if (text) {
              resultHTML += '<div>' + this.sanitizer.sanitize(SecurityContext.HTML, text) + '</div>';
            }
          });
          return resultHTML;
        }),
      );
    }

    // default
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.DELAY_UNTIL_EVENT');
  }

  getTemplateActionSubtitle(
    namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntil: boolean,
    automation: AutomationInterface,
  ): Observable<string> {
    return this.getActionSubtitleSwitcher(namespace, step, true, automation);
  }

  getActionSubtitle(
    namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntil: boolean,
    automation: AutomationInterface,
  ): Observable<string> {
    return this.getActionSubtitleSwitcher(namespace, step, false, automation);
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getWaitForTriggerTaskDefinitionFromStep(step: WorkflowStepInterface): WorkflowTriggerInterface {
    for (const key in WORKFLOW_TRIGGERS) {
      if (key === step?.waitForTriggerTaskDefinitionId) {
        return WORKFLOW_TRIGGERS[key];
      }
    }
    return null;
  }

  getSleepDurationText(nanos: number): Observable<string> {
    const timeUnit = getLargestTimeUnit(nanos);
    const sleepInTimeUnit = toTimeUnit(nanos, timeUnit);
    if (sleepInTimeUnit > 0) {
      let params = {};
      const keys = {};
      if (sleepInTimeUnit === 1) {
        keys[TimeUnit.Minute] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.TIME_PERIOD.WAIT_ONE_MINUTE';
        keys[TimeUnit.Hour] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.TIME_PERIOD.WAIT_ONE_HOUR';
        keys[TimeUnit.Day] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.TIME_PERIOD.WAIT_ONE_DAY';
      } else {
        keys[TimeUnit.Minute] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.TIME_PERIOD.WAIT_MINUTES';
        keys[TimeUnit.Hour] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.TIME_PERIOD.WAIT_HOURS';
        keys[TimeUnit.Day] = 'AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.TIME_PERIOD.WAIT_DAYS';
        params = { time: sleepInTimeUnit };
      }
      return this.translateService.stream(keys[timeUnit] || keys[TimeUnit.Hour], params);
    }
    return of('');
  }

  getWaitForEventData(automation: AutomationInterface, waitForTriggerFilterValue: string): string {
    const chosenStep = getWaitForTriggerFilterStep(waitForTriggerFilterValue, automation);
    return chosenStep?.data || '';
  }

  getWaitForDataDisplay(automation: AutomationInterface, waitForTriggerFilterValue: string): Observable<string> {
    if (!waitForTriggerFilterValue) {
      return of('');
    }

    const chosenWorkflowStep = getWaitForTriggerFilterStep(waitForTriggerFilterValue, automation);
    const chosenWorkflowStepName =
      chosenWorkflowStep?.name || this.translateService.instant('AUTOMATIONS.COMMON.TRIGGER');

    let chosenWorkflowStepTaskDefinition$: Observable<TaskDefinition | null> = of(null);
    if (chosenWorkflowStep?.taskDefinitionId) {
      chosenWorkflowStepTaskDefinition$ = this.automataDevService.getTaskDefinition(
        chosenWorkflowStep.taskDefinitionId,
      );
    }

    const chosenWorkflowDataType$ = chosenWorkflowStepTaskDefinition$.pipe(
      take(1),
      map((taskDefinition) => {
        // The step being passed in here should be the automation step defintion not the activity step
        const outputParam = getWaitForTriggerFilterStepOutputParam(
          waitForTriggerFilterValue,
          taskDefinition?.outputParameters || [],
          convertEntityTypeToDataType(automation.entityType),
        );
        if (isDefaultWaitForTriggerStepAndOutputParam(waitForTriggerFilterValue, outputParam, automation.entityType)) {
          return null;
        }
        return outputParam;
      }),
      map((outputParam) => {
        return outputParam?.settings?.name;
      }),
    );

    return chosenWorkflowDataType$.pipe(
      take(1),
      switchMap((dataType) => {
        return this.getWaitForDisplayForNameAndType(chosenWorkflowStepName, dataType);
      }),
    );
  }

  getWaitForDisplayForNameAndType(name: string, dataTypeName): Observable<string> {
    if (!!name && !!dataTypeName) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.DELAY_UNTIL_EVENT.WAIT_FOR_DATA_PARAM', {
        dataType: dataTypeName,
        name: name,
      });
    }
    return of('');
  }
}

export function getWaitForTriggerFilterStep(
  waitForTriggerFilterDataValue: string,
  automation: AutomationInterface,
): WorkflowStepInterface {
  const waitForTriggerFilterStepId = extractWorkflowStepIdFromWaitForTriggerFilter(waitForTriggerFilterDataValue);
  if (waitForTriggerFilterStepId === TRIGGER_STEP_ID) {
    return automation.triggerStep;
  }
  return automation.workflow.find((s) => s.id == waitForTriggerFilterStepId);
}

export function getWaitForTriggerFilterStepOutputParam(
  waitForTriggerFilterDataValue: string,
  outputParameters: OutputParameter[],
  dataType: DataType,
): OutputParameter {
  const triggerKey = extractWorkflowStepParameterKeyFromReplacementTag(waitForTriggerFilterDataValue);
  if (triggerKey) {
    return outputParameters?.find((o) => o.settings.id === triggerKey);
  }
  return outputParameters?.find((o) => o.settings.dataType === dataType);
}

function isDefaultWaitForTriggerStepAndOutputParam(
  waitForTriggerFilterDataValue: string,
  outputParam: OutputParameter,
  entityType: EntityType,
): boolean {
  const waitForTriggerFilterStepId = extractWorkflowStepIdFromWaitForTriggerFilter(waitForTriggerFilterDataValue);
  return (
    waitForTriggerFilterStepId === TRIGGER_STEP_ID &&
    outputParam?.settings?.dataType === convertEntityTypeToDataType(entityType)
  );
}
